"use client";

import { useState, useEffect, useCallback } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Blogimg from "@/../public/assets/images/blogSample.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { getSafeImageSrc, createImageErrorHandler } from "@/components/layout/imageUtils";
import Link from "next/link";

import "swiper/css";
import "swiper/css/navigation";
import { API_BASE_URL, CDN_BASE_URL, USE_BACKEND } from "@/app/api/variable";
import blogData from "@/app/data/blog.json";

type Blog = {
  _id: string;
  title: string;
  content: string;
  image: string;
  link?: string;
};

type PaginatedResponse = {
  data: Blog[];
  totalPages: number;
  totalItems: number;
};

export default function BlogSlider() {
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [page] = useState(1);
  const [mounted, setMounted] = useState(false);
  const limit = 10;

  // Mark component as mounted (client-side only)
  useEffect(() => {
    setMounted(true);
  }, []);

  // Fetch blogs from API (use CDN for images)
  const fetchBlogs = useCallback(async () => {
    try {
      if(USE_BACKEND){
      const res = await axios.get<PaginatedResponse>(
        `${API_BASE_URL}/blogs?page=${page}&limit=${limit}&sortBy=createdAt&order=desc`
      );

      const blogsWithCdnImages = res.data.data.map(blog => ({
        ...blog,
        image: blog.image ? `${CDN_BASE_URL}/${blog.image}` : Blogimg.src,
      }));

      setBlogs(blogsWithCdnImages);
    }else{
      let data: Blog[] = []
      for (let i = 0; i <= 2; i++) {
        if(blogData[i]){
          data.push(blogData[i]);
        }
      }
      setBlogs(data as Blog[]);
    }
    } catch (error) {
      console.error("Error fetching blogs:", error);
    }
  }, [page, limit]);

  useEffect(() => {
    if (mounted) {
      fetchBlogs();
    }
  }, [mounted, fetchBlogs]);

  // Don't render until mounted to prevent hydration mismatch
  if (!mounted) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[1, 2, 3].map((i) => (
          <div key={i} className="blog animate-pulse">
            <div className="w-full h-52 bg-gray-300 rounded"></div>
            <div className="info-sec flex flex-col flex-grow p-4">
              <div className="h-6 bg-gray-300 rounded mb-2"></div>
              <div className="h-4 bg-gray-300 rounded mb-2"></div>
              <div className="h-4 bg-gray-300 rounded mb-2"></div>
              <div className="h-4 bg-gray-300 rounded w-2/3"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="relative">
      {blogs.length > 0 && (
        <>
        {mounted && (

          <Swiper
            modules={[Navigation]}
            navigation={{ prevEl: ".prev-btn", nextEl: ".next-btn" }}
            spaceBetween={20}
            slidesPerView={3}
            loop={true}
            breakpoints={{
              0: { slidesPerView: 1 },
              768: { slidesPerView: 2 },
              1024: { slidesPerView: 3 },
            }}
          >
            {blogs.map((blog) => (
              <SwiperSlide key={blog._id}>
                <Link href={`/blogs/${blog._id}`} className="blog block hover:shadow-lg transition-shadow duration-300">
                  <img
                    src={getSafeImageSrc(`https://assets.nimbusnode.dev/${blog.image}`, Blogimg.src)}
                alt={blog.title || 'Blog post'}
                    className="w-full h-52 object-cover"
                    onError={createImageErrorHandler(Blogimg.src)}
                  />
                  <div className="info-sec flex flex-col flex-grow p-4">
                    <h3 className="text-base font-semibold mb-2">{blog.title}</h3>
                    <p className="text-gray-600 text-sm flex-grow">{blog.content}</p>
                    <div className="learn-link flex items-center gap-1 mt-2">
                      <span>Learn More</span>
                      <FontAwesomeIcon icon={faArrowRight} />
                    </div>
                  </div>
                </Link>
              </SwiperSlide>
            ))}
          </Swiper>
        )}

      {/* Navigation Arrows */}
      {/* Navigation Arrows */}
      <button className="prev-btn hidden md:flex absolute left-[-40px] top-1/2 -translate-y-1/2 z-10 bg-white p-2 rounded-full shadow-md" suppressHydrationWarning>
            <ChevronLeft className="w-5 h-5 text-blue-600" />
          </button>
      <button className="next-btn hidden md:flex absolute right-[-40px] top-1/2 -translate-y-1/2 z-10 bg-white p-2 rounded-full shadow-md" suppressHydrationWarning>
            <ChevronRight className="w-5 h-5 text-blue-600" />
          </button>
        </>
      )}
    </div>
  );
}
